import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { LoadManager } from '@theme/custom-collection-utility';

/**
 * Theme setting: Alle/Aanbevolen & ecomm collection recommendations (default true when unset).
 * @returns {boolean}
 */
function isEcommCollectionRecommendationsEnabled() {
  return /** @type {any} */ (window).themeSettings?.enableEcommCollectionRecommendations !== false;
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {boolean} */
  #ownsPaginationLoader = false;

  /** @type {number} */
  #paginationLoaderShownAt = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #paginationLoaderHideTimer = null;

  /** @type {boolean} */
  #pagingInProgress = false;

  /** @type {boolean} */
  #scrollUrlSyncScheduled = false;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#alignLandingPageScroll();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener('collection-results-rendered', this.#handleCollectionResultsRendered);
    window.addEventListener('scroll', this.#scheduleScrollUrlSync, { passive: true });
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener('collection-results-rendered', this.#handleCollectionResultsRendered);
    window.removeEventListener('scroll', this.#scheduleScrollUrlSync);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;
          if (this.#pagingInProgress) return;

          const { viewMorePrevious: previousSentinel, viewMoreNext: nextSentinel } = this.refs;
          const shouldLoadPrevious = entries.some(
            (entry) => entry.isIntersecting && entry.target === previousSentinel
          );
          const shouldLoadNext = entries.some((entry) => entry.isIntersecting && entry.target === nextSentinel);

          if (!shouldLoadPrevious && !shouldLoadNext) return;

          this.#pagingInProgress = true;
          try {
            // Keep loading while the sentinel stays in range (IO often does not re-fire after prepend/append).
            if (shouldLoadPrevious) {
              while (this.#isSentinelInLoadRange(this.refs.viewMorePrevious)) {
                const rendered = await this.#renderPreviousPage();
                if (!rendered) break;
              }
            } else if (shouldLoadNext) {
              while (this.#isSentinelInLoadRange(this.refs.viewMoreNext)) {
                const rendered = await this.#renderNextPage();
                if (!rendered) break;
              }
            }
          } finally {
            this.#pagingInProgress = false;
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {Element | undefined} sentinel
   * @returns {boolean}
   */
  #isSentinelInLoadRange(sentinel) {
    if (!(sentinel instanceof HTMLElement) || !sentinel.isConnected) return false;
    const margin = 100;
    const rect = sentinel.getBoundingClientRect();
    return rect.bottom >= -margin && rect.top <= window.innerHeight + margin;
  }

  /**
   * On ?page=N refresh, place the first product of that page just under the sticky header/filters
   * with a small peek of the row above — matches the pre-refresh stop position.
   */
  #alignLandingPageScroll() {
    const { grid } = this.refs;
    const pageParam = grid?.dataset.pageParam || 'page';
    const landingPage = Number(new URL(window.location.href).searchParams.get(pageParam));
    if (!grid || !Number.isFinite(landingPage) || landingPage <= 1) return;

    const align = () => {
      if (!this.isConnected) return;
      const card = grid.querySelector(`:scope > [data-page="${landingPage}"]`);
      if (!(card instanceof HTMLElement)) return;

      const stickyOffset = this.#getCollectionStickyOffset();
      const previousRowPeek = 120;
      const top = window.scrollY + card.getBoundingClientRect().top - stickyOffset - previousRowPeek;
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    };

    requestAnimationFrame(() => {
      align();
      // Re-run after previous pages may have prepended (keeps the same visual stop).
      setTimeout(align, 300);
    });
  }

  /**
   * @returns {number}
   */
  #getCollectionStickyOffset() {
    let offset = 12;
    const header = document.querySelector('header-component, sticky-header, #header-group, .header');
    if (header instanceof HTMLElement) {
      offset += Math.round(header.getBoundingClientRect().height);
    }
    const filters = document.querySelector('quick-filters-component, .quick-filters');
    if (filters instanceof HTMLElement) {
      const rect = filters.getBoundingClientRect();
      if (rect.height > 0 && rect.top < 220) {
        offset += Math.round(rect.height);
      }
    }
    return offset;
  }

  #scheduleScrollUrlSync = () => {
    if (this.#scrollUrlSyncScheduled || this.#pagingInProgress) return;
    this.#scrollUrlSyncScheduled = true;
    requestAnimationFrame(() => {
      this.#scrollUrlSyncScheduled = false;
      this.#syncUrlToVisiblePage();
    });
  };

  #syncUrlToVisiblePage() {
    const { grid } = this.refs;
    if (!grid || !this.isConnected) return;

    const pageParam = grid.dataset.pageParam || 'page';
    const anchorY = this.#getCollectionStickyOffset();
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"][data-page], :scope > [data-page]');

    let bestPage = null;
    let bestDist = Infinity;

    for (const card of cards) {
      if (!(card instanceof HTMLElement) || card.classList.contains('no-disp')) continue;
      const rect = card.getBoundingClientRect();
      if (rect.bottom <= 0) continue;
      if (rect.top > window.innerHeight) break;

      const page = Number(card.dataset.page);
      if (!Number.isFinite(page) || page < 1) continue;

      const dist = Math.abs(rect.top - anchorY);
      if (dist < bestDist) {
        bestDist = dist;
        bestPage = page;
      }
    }

    if (bestPage == null) return;

    const current = Number(new URL(window.location.href).searchParams.get(pageParam));
    if (current === bestPage) return;

    history.replaceState('', '', this.#buildBrowserHistoryURL(bestPage).toString());
  }

  #resetObservedViewMore() {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }

    this.#storeImageRatioSettings();
    this.#observeViewMore();
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * URL used for Section Rendering API requests. Must include active facet params even when the
   * browser URL is intentionally cleaned (e.g. disable-url-updates / step flow).
   *
   * @param {number} pageNumber
   * @returns {URL}
   */
  #buildSectionFetchURL(pageNumber) {
    const url = new URL(window.location.pathname, window.location.origin);
    const pageParam = this.refs.grid?.dataset.pageParam || 'page';
    url.searchParams.set(pageParam, String(pageNumber));

    const facetsForm =
      document.querySelector('#filters-drawer facets-form-component') ||
      document.querySelector('facets-form-component');

    if (facetsForm && typeof /** @type {any} */ (facetsForm).createURLParameters === 'function') {
      const params = /** @type {any} */ (facetsForm).createURLParameters();
      for (const [key, value] of params.entries()) {
        url.searchParams.append(key, value);
      }
    }

    url.hash = '';
    return url;
  }

  /**
   * Visible URL after loading another page (matches facets.js: avoid leaking full filter state).
   *
   * @param {number} pageNumber
   * @returns {URL}
   */
  #buildBrowserHistoryURL(pageNumber) {
    const url = new URL(window.location.pathname, window.location.origin);
    const pageParam = this.refs.grid?.dataset.pageParam || 'page';
    url.searchParams.set(pageParam, String(pageNumber));

    const RECO_PARAM = 'filter.p.m.custom.product_recommendations';
    const aanbevolenInput = document.querySelector('#filters-drawer input[data-label="Aanbevolen"]');
    if (
      isEcommCollectionRecommendationsEnabled() &&
      aanbevolenInput instanceof HTMLInputElement &&
      aanbevolenInput.checked
    ) {
      url.searchParams.set(RECO_PARAM, 'Aanbevolen');
    }

    url.hash = '';
    return url;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      pageInfo.url = this.#buildSectionFetchURL(pageNumber);
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @returns {Promise<boolean>}
   */
  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    this.#showPaginationLoader();

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return false;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return false;
      }

      const uniquePageItemElements = this.#filterDuplicatePageItems(nextPageItemElements);
      if (!uniquePageItemElements.length) {
        this.pages.delete(nextPage.page);
        return false;
      }

      grid.append(...uniquePageItemElements);

      await this.#processNewElements(nextPage.page, 'next');

      // Keep address bar clean (same rules as facets); fetches use #buildSectionFetchURL.
      history.pushState('', '', this.#buildBrowserHistoryURL(nextPage.page).toString());

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });

      return true;
    } finally {
      this.#hidePaginationLoader();
    }
  }

  /**
   * @returns {Promise<boolean>}
   */
  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    this.#showPaginationLoader();

    try {
      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return false;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return false;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      const uniquePageItemElements = this.#filterDuplicatePageItems(previousPageItemElements);
      if (!uniquePageItemElements.length) {
        this.pages.delete(previousPage.page);
        return false;
      }

      grid.prepend(...uniquePageItemElements);

      await this.#processNewElements(previousPage.page, 'previous');

      // Update URL when older pages are loaded (scroll-up → ?page=3, ?page=2, …).
      history.pushState('', '', this.#buildBrowserHistoryURL(previousPage.page).toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });

      return true;
    } finally {
      this.#hidePaginationLoader();
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   * @param {number} page
   * @param {"previous" | "next"} direction
   * @returns {Promise<void>}
   */
  #processNewElements(page, direction) {
    return new Promise((resolve) => {
      // Wait for the DOM to update before running recommendation/order side effects.
      requestAnimationFrame(() => {
        this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
        this.#syncAppendedProductCards(page, direction);
        requestAnimationFrame(() => resolve());
      });
    });
  }

  /**
   * Keep appended product cards aligned with cart/subscription/recommendation state.
   * @param {number} page
   * @param {"previous" | "next"} direction
   */
  #syncAppendedProductCards(page, direction) {
    const anchor = direction === 'next' ? this.#getViewportAnchor() : null;

    window.dispatchEvent(
      new CustomEvent('collection-page-appended', {
        detail: {
          page,
          direction,
          sectionId: this.sectionId,
        },
      })
    );

    if (anchor) this.#restoreViewportAnchor(anchor);
    this.#scheduleProductCardQuantitySync();
  }

  /**
   * Capture a visible grid item so recommendation reordering can preserve the user's viewport.
   * @returns {{ element: Element, top: number } | null}
   */
  #getViewportAnchor() {
    const { grid } = this.refs;
    if (!grid) return null;

    const viewportPadding = 80;
    const candidates = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')).filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.bottom > viewportPadding && rect.top < window.innerHeight;
    });

    const element = candidates[0];
    if (!element) return null;

    return {
      element,
      top: element.getBoundingClientRect().top,
    };
  }

  /**
   * @param {{ element: Element, top: number }} anchor
   */
  #restoreViewportAnchor(anchor) {
    const newTop = anchor.element.getBoundingClientRect().top;
    const delta = newTop - anchor.top;
    if (!delta) return;

    window.scrollBy({
      top: delta,
      behavior: 'instant',
    });
  }

  #scheduleProductCardQuantitySync() {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const productCardUpdater = /** @type {any} */ (window).ProductCardUpdater;
        if (productCardUpdater && typeof productCardUpdater.updateAllProductCardsWithQuantities === 'function') {
          productCardUpdater.updateAllProductCardsWithQuantities(true);
        }
      }, 0);
    });
  }

  #showPaginationLoader() {
    if (this.#paginationLoaderHideTimer) {
      clearTimeout(this.#paginationLoaderHideTimer);
      this.#paginationLoaderHideTimer = null;
    }

    if (this.#ownsPaginationLoader || document.getElementById('load-container')) return;

    this.#ownsPaginationLoader = true;
    this.#paginationLoaderShownAt = Date.now();
    LoadManager.addLoader();
  }

  #hidePaginationLoader() {
    if (!this.#ownsPaginationLoader) return;

    const MIN_LOADER_VISIBLE_MS = 350;
    const elapsed = Date.now() - this.#paginationLoaderShownAt;
    const remaining = Math.max(0, MIN_LOADER_VISIBLE_MS - elapsed);

    const removeLoader = () => {
      this.#paginationLoaderHideTimer = null;
      if (!this.#ownsPaginationLoader) return;

      this.#ownsPaginationLoader = false;
      LoadManager.removeLoader();
    };

    if (remaining > 0) {
      this.#paginationLoaderHideTimer = setTimeout(removeLoader, remaining);
      return;
    }

    removeLoader();
  }

  /**
   * Avoid appending duplicate product cards when recommendation views reorder or use nested paginates.
   * @param {NodeListOf<Element>} pageItemElements
   * @returns {Element[]}
   */
  #filterDuplicatePageItems(pageItemElements) {
    const { grid } = this.refs;
    if (!grid) return Array.from(pageItemElements);

    const existingProductIds = new Set(
      Array.from(grid.querySelectorAll(':scope > [data-product-id]'))
        .map((element) => element.getAttribute('data-product-id'))
        .filter(Boolean)
    );

    /** @type {Element[]} */
    const uniqueItems = [];
    pageItemElements.forEach((element) => {
      const productId = element.getAttribute('data-product-id');
      if (productId && existingProductIds.has(productId)) return;
      if (productId) existingProductIds.add(productId);
      uniqueItems.push(element);
    });

    return uniqueItems;
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '157/105',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    const cards = /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      return !element.classList.contains('no-disp');
    }));

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = this.#buildSectionFetchURL(page);

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    this.#resetObservedViewMore();

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      const gridChanged = mutations.some((mutation) => mutation.type === 'childList' || mutation.type === 'attributes');

      if (gridChanged) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#resetObservedViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
        if (this.isConnected) {
          this.#resetObservedViewMore();
          this.#fetchPage('next');
        }
      }, 3000);
    }
  };

  #handleCollectionResultsRendered = (/** @type {CustomEvent<{ sectionId?: string }>} */ event) => {
    const sectionId = event?.detail?.sectionId;
    if (sectionId && sectionId !== this.sectionId) return;

    this.#resetPaginationState();

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#resetObservedViewMore();
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#alignLandingPageScroll();
    });
  };

  #resetPaginationState() {
    this.pages.clear();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
  }
}
